import React from "react";
import { useTranslation } from "react-i18next";
import "./styles/index.css";
import "./styles/pages/AdminPage.css";
import { HostTeamInfo, AppSettings, GraphUser, Administrator, TeamPrivacyType, TeamPrivacy, QuotaModel, QuotaModelCreateInput, StorageQuotaMode, SensitivityLabelMode, ExtensionType, ExtensionsStatusData } from "./types";
import { apiService } from "./services/api.service";
import { telemetryService } from "./services/telemetry.service";
import PeoplePicker from "./PeoplePicker";
import ApprovedRequestsManager from "./ApprovedRequestsManager";
import { SubscriptionCard } from "./components/SubscriptionCard";
import {
  PrivacyTypesSection,
  AdministratorsSection,
  ApproversSection,
  SecurityGroupSection,
  QuotaModelsSection,
  SensitivityLabelsSection,
} from "./components/admin";
import { useFeature } from "./hooks";
import { FEATURE_KEYS } from "./types";

interface AdminPageProps {
  theme?: string;
  teamId: string | null;
  currentUserId?: string;
}

export default function AdminPage({ theme = "default", teamId, currentUserId }: AdminPageProps) {
  const { i18n, t } = useTranslation('adminPage');
  
  // 🔍 DEBUG: Log component mount and language changes
  React.useEffect(() => {
    console.log('[AdminPage] Component mounted with language:', i18n.language);
    
    const handleLanguageChange = (lng: string) => {
      console.log('[AdminPage] Language changed to:', lng);
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const [teamInfo, setTeamInfo] = React.useState<HostTeamInfo | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [settings, setSettings] = React.useState<AppSettings | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const [selectedApprovers, setSelectedApprovers] = React.useState<GraphUser[]>([]);
  const [showPeoplePicker, setShowPeoplePicker] = React.useState(false);

  // 🚩 FEATURE FLAGS
  const isQuotaFeatureEnabled = useFeature(FEATURE_KEYS.QUOTA);
  const isGroupLockFeatureEnabled = useFeature(FEATURE_KEYS.ENTRA_ID_GROUP_CREATION_LOCK);
  const isSensitivityFeatureEnabled = useFeature(FEATURE_KEYS.SENSITIVITY_LABELS);

  // États pour les extensions modulaires Multi-Apps
  const [extensionsStatus, setExtensionsStatus] = React.useState<ExtensionsStatusData | null>(null);
  const [loadingExtensions, setLoadingExtensions] = React.useState(false);
  const [verifyingExtension, setVerifyingExtension] = React.useState<ExtensionType | null>(null);

  // États pour la restriction de création de groupes (Security Group)
  const [allowedSecurityGroup, setAllowedSecurityGroup] = React.useState<GraphUser | null>(null);
  const [loadingSecurityGroup, setLoadingSecurityGroup] = React.useState(false);
  const [showSecurityGroupPicker, setShowSecurityGroupPicker] = React.useState(false);
  const [showRemoveSecurityGroupConfirm, setShowRemoveSecurityGroupConfirm] = React.useState(false);

  // États pour les administrateurs
  const [administrators, setAdministrators] = React.useState<Administrator[]>([]);
  const [showAdminPeoplePicker, setShowAdminPeoplePicker] = React.useState(false);

  // États pour les types d'équipes
  const [allPrivacyTypes, setAllPrivacyTypes] = React.useState<TeamPrivacyType[]>([]);
  const [enabledPrivacyTypes, setEnabledPrivacyTypes] = React.useState<TeamPrivacy[]>([]);

  // États pour les modèles de quota
  const [quotaModels, setQuotaModels] = React.useState<QuotaModel[]>([]);
  const [loadingQuotaModels, setLoadingQuotaModels] = React.useState(false);
  const [showQuotaModal, setShowQuotaModal] = React.useState(false);
  const [editingQuotaModel, setEditingQuotaModel] = React.useState<QuotaModel | null>(null);
  const [quotaForm, setQuotaForm] = React.useState<QuotaModelCreateInput>({
    name: "",
    maxStorageGB: 100,
    notificationsEnabled: false,
    notificationThresholdPercent: null,
    isDefault: false,
  });
  const [quotaFormError, setQuotaFormError] = React.useState<string | null>(null);
  const [quotaFormSaving, setQuotaFormSaving] = React.useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = React.useState(false);
  const [showNoQuotaConfirmModal, setShowNoQuotaConfirmModal] = React.useState(false);

  // État pour la modal d'avertissement
  const [warningModal, setWarningModal] = React.useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "warning" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "warning"
  });

  // État pour la modal de confirmation de changement de mode d'approbation
  const [approvalModeConfirmModal, setApprovalModeConfirmModal] = React.useState<{
    isOpen: boolean;
    newMode: "basic" | "basicAwaitAll" | null;
    impactedRequests: Array<{
      id: string;
      teamName: string;
      approvedCount: number;
      totalApprovers: number;
      requesterEmail: string;
      privacy: TeamPrivacy;
    }>;
  }>({
    isOpen: false,
    newMode: null,
    impactedRequests: []
  });

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

  // Fonction helper pour afficher une modal d'avertissement
  const showWarning = (title: string, message: string, type: "warning" | "error" = "warning") => {
    setWarningModal({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeWarningModal = () => {
    setWarningModal({
      ...warningModal,
      isOpen: false
    });
  };

  React.useEffect(() => {
    // Charger les paramètres même sans équipe détectée
    loadSettings();
    
    // Si une équipe est détectée, charger ses infos aussi
    if (teamId) {
      loadTeamInfo();
    } else {
      // Mode autonome - pas besoin d'équipe
      setLoading(false);
    }
  }, [teamId]);


  const loadTeamInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getHostTeamInfo(teamId || '');

      if (data.success && data.data) {
        setTeamInfo(data.data);
      } else {
        setError(data.error || t('messages.error'));
      }
    } catch (err: any) {
      console.error("Error loading team info:", err);
      setError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const resp = await apiService.getSettings();
      if (resp.success) {
        const loadedSettings = resp.data || { enabledTemplates: [], notificationSettings: {}, azureTenantId: '' };
        setSettings(loadedSettings);

        // Charger les approbateurs définis dans les paramètres
        if (loadedSettings.approvers && loadedSettings.approvers.length > 0) {
          setSelectedApprovers(loadedSettings.approvers.map(approver => ({
            id: approver.id,
            displayName: approver.displayName,
            mail: approver.email,
            userPrincipalName: approver.email
          })));
        }

        // Charger les types de confidentialité activés
        setEnabledPrivacyTypes(loadedSettings.enabledPrivacyTypes || ['private', 'public']);

        // Charger le groupe de sécurité autorisé depuis settings
        if (loadedSettings.allowedSecurityGroup) {
          setAllowedSecurityGroup(loadedSettings.allowedSecurityGroup);
        }
      }

      // Charger les administrateurs
      const adminsResp = await apiService.getAllAdministrators();
      if (adminsResp.success && adminsResp.data) {
        setAdministrators(adminsResp.data);
      }

      // Charger tous les types de confidentialité disponibles
      const typesResp = await apiService.getAllTeamPrivacyTypes();
      if (typesResp.success && typesResp.data) {
        setAllPrivacyTypes(typesResp.data);
      }

      // Charger le statut des extensions modulaires en premier pour connaître l'état des features et du plan
      const extStatus = await loadExtensionsStatus();

      // Vérifier si la feature quota est utilisable (active + éligible au plan payant si isPremium)
      const quotaDetail = extStatus?.featureDetails?.find(f => f.key === FEATURE_KEYS.QUOTA);
      const isQuotaEnabled = quotaDetail ? quotaDetail.enabled : isQuotaFeatureEnabled;
      const isQuotaPremium = quotaDetail?.isPremium ?? false;
      const isPaid = extStatus?.isPaidTier ?? false;
      const canAccessQuota = isQuotaEnabled && (!isQuotaPremium || isPaid);

      if (canAccessQuota) {
        const quotaResp = await apiService.listQuotaModels();
        if (quotaResp.success && quotaResp.data) {
          setQuotaModels(quotaResp.data);
        }
      }

      // Vérifier si la feature de verrouillage de création de groupe est activée
      const groupLockDetail = extStatus?.featureDetails?.find(f => f.key === FEATURE_KEYS.ENTRA_ID_GROUP_CREATION_LOCK);
      const isGroupLockEnabled = groupLockDetail ? groupLockDetail.enabled : isGroupLockFeatureEnabled;

      if (isGroupLockEnabled) {
        await loadAllowedSecurityGroup();
      }

    } catch (e) {
      console.error("Error loading settings", e);
    }
  };

  const loadExtensionsStatus = async (): Promise<ExtensionsStatusData | null> => {
    try {
      setLoadingExtensions(true);
      const resp = await apiService.getExtensionsStatus();
      if (resp.success && resp.data) {
        setExtensionsStatus(resp.data);
        return resp.data;
      } else if ((resp as any).code === 'PAID_PLAN_REQUIRED' || resp.error?.includes('payant') || !resp.success) {
        const fallback: ExtensionsStatusData = {
          isPaidTier: false,
          extensions: {
            sharepoint: {
              extensionType: 'sharepoint',
              displayName: 'RequestATeam - Extension Stockage & Quotas SharePoint',
              description: '',
              clientId: '',
              consented: false,
              requiredRoles: [],
              grantedRoles: [],
              consentUrl: '',
              isPaidTier: false,
            },
            directory: {
              extensionType: 'directory',
              displayName: 'RequestATeam - Extension Répertoire & Gouvernance Entra ID',
              description: '',
              clientId: '',
              consented: false,
              requiredRoles: [],
              grantedRoles: [],
              consentUrl: '',
              isPaidTier: false,
            },
            sensitivity: {
              extensionType: 'sensitivity',
              displayName: 'RequestATeam - Extension Sensitivity Labels Purview',
              description: '',
              clientId: '',
              consented: false,
              requiredRoles: [],
              grantedRoles: [],
              consentUrl: '',
              isPaidTier: false,
            },
          },
        };
        setExtensionsStatus(fallback);
        return fallback;
      }
      return null;
    } catch (e) {
      console.error("Error loading extensions status", e);
      return null;
    } finally {
      setLoadingExtensions(false);
    }
  };

  const loadAllowedSecurityGroup = async () => {
    try {
      setLoadingSecurityGroup(true);
      const resp = await apiService.getAllowedSecurityGroup();
      if (resp.success && resp.data) {
        const group: GraphUser | null = resp.data.allowedSecurityGroup || (resp.data.id ? {
          id: resp.data.id,
          displayName: resp.data.displayName || '',
          mail: resp.data.mail || '',
          userPrincipalName: resp.data.mail || '',
        } : null);
        setAllowedSecurityGroup(group);
      }
    } catch (e) {
      console.error("Error loading allowed security group", e);
    } finally {
      setLoadingSecurityGroup(false);
    }
  };

  const handleActivateExtension = async (extensionType: ExtensionType) => {
    try {
      const resp = await apiService.getExtensionConsentUrl(extensionType);
      if (resp.success && resp.data?.consentUrl) {
        window.open(resp.data.consentUrl, '_blank');
      } else {
        const directUrl = extensionsStatus?.extensions[extensionType]?.consentUrl;
        if (directUrl) {
          window.open(directUrl, '_blank');
        } else {
          showWarning(
            t('sections.securityGroup.extensionRequiredTitle'),
            resp.error || t('messages.recordSaveErrorMessage'),
            "error"
          );
        }
      }
    } catch (err: any) {
      console.error("Error getting consent URL:", err);
      showWarning(
        t('sections.securityGroup.extensionRequiredTitle'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    }
  };

  const handleVerifyExtension = async (extensionType: ExtensionType) => {
    try {
      setVerifyingExtension(extensionType);
      const resp = await apiService.verifyExtension(extensionType);
      if (resp.success && resp.data?.consented) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        await loadExtensionsStatus();
        if (extensionType === 'directory') {
          await loadAllowedSecurityGroup();
        }
      } else {
        showWarning(
          t('sections.securityGroup.extensionRequiredTitle'),
          t('messages.extensionVerifiedError'),
          "warning"
        );
        await loadExtensionsStatus();
      }
    } catch (err: any) {
      console.error("Error verifying extension:", err);
      showWarning(
        t('sections.securityGroup.extensionRequiredTitle'),
        t('messages.extensionVerifiedError'),
        "error"
      );
    } finally {
      setVerifyingExtension(null);
    }
  };

  const handleSetAllowedSecurityGroup = async (group: GraphUser) => {
    setSaving(true);
    setShowSecurityGroupPicker(false);
    try {
      const resp = await apiService.setAllowedSecurityGroup(group);
      if (resp.success) {
        setAllowedSecurityGroup(group);
        setSaveSuccess(true);
        telemetryService.capture('admin_security_group_updated', currentUserId || '', {
          properties: {
            group_id: group.id,
            group_name: group.displayName,
          }
        });
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.securityGroupError'),
          "error"
        );
      }
    } catch (err: any) {
      console.error("Error setting allowed security group:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.securityGroupError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAllowedSecurityGroup = async () => {
    setShowRemoveSecurityGroupConfirm(false);
    setSaving(true);
    try {
      const resp = await apiService.removeAllowedSecurityGroup();
      if (resp.success) {
        setAllowedSecurityGroup(null);
        setSaveSuccess(true);
        telemetryService.capture('admin_security_group_removed', currentUserId || '', {});
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err: any) {
      console.error("Error removing allowed security group:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  // Helper function to get privacy type label with icon
  const getPrivacyLabel = (privacy: TeamPrivacy): string => {
    switch (privacy) {
      case 'private':
        return t('messages.privacyLabels.private');
      case 'public':
        return t('messages.privacyLabels.public');
      default:
        return privacy;
    }
  };

  const onChangeApprovalMode = async (mode: "basic" | "basicAwaitAll") => {
    if (!settings) return;

    const currentMode = settings.notificationSettings?.approvalMode || 'basic';

    // Si on passe de basicAwaitAll à basic, afficher la modal de confirmation
    if (currentMode === 'basicAwaitAll' && mode === 'basic') {
      // Récupérer les demandes en attente qui seront impactées
      try {
        const requestsResp = await apiService.getRequests();
        if (requestsResp.success && requestsResp.data) {
          const pendingRequests = requestsResp.data.filter((r: any) => r.status === 'Pending');

          // Trouver les demandes qui ont au moins une approbation
          const impactedRequests = pendingRequests
            .filter((r: any) => {
              const responses = r.approvalResponses || [];
              if (responses.length === 0) return false;

              const approvedCount = responses.filter((resp: any) => resp.response === 'approved').length;
              return approvedCount > 0 && approvedCount < responses.length;
            })
            .map((r: any) => {
              const responses = r.approvalResponses || [];
              const approvedCount = responses.filter((resp: any) => resp.response === 'approved').length;

              return {
                id: r.id,
                teamName: r.teamName,
                approvedCount,
                totalApprovers: responses.length,
                requesterEmail: r.requesterEmail,
                privacy: r.privacy
              };
            });

          // Afficher la modal de confirmation avec la liste des demandes impactées
          setApprovalModeConfirmModal({
            isOpen: true,
            newMode: mode,
            impactedRequests
          });
          return; // Attendre la confirmation de l'utilisateur
        }
      } catch (err: any) {
        console.error("Error fetching requests:", err);
      }
    }

    // Sinon, procéder directement au changement
    await confirmApprovalModeChange(mode);
  };

  const confirmApprovalModeChange = async (mode: "basic" | "basicAwaitAll") => {
    if (!settings) return;
    setSaving(true);
    setSaveSuccess(false);

    // Fermer la modal de confirmation
    setApprovalModeConfirmModal({
      isOpen: false,
      newMode: null,
      impactedRequests: []
    });

    try {
      const newSettings: AppSettings = {
        ...settings,
        notificationSettings: { ...(settings.notificationSettings || {}), approvalMode: mode },
      };
      const resp = await apiService.updateSettings(newSettings);
      if (resp.success && resp.data) {
        setSettings(resp.data);

        telemetryService.capture('admin_approval_mode_changed', currentUserId || '', {
          properties: {
            old_mode: settings?.notificationSettings?.approvalMode || 'basic',
            new_mode: mode,
            impacted_requests_count: approvalModeConfirmModal.impactedRequests.length || 0,
            auto_reevaluated: false,
          }
        });

        // Déclencher automatiquement la réévaluation des demandes en cours
        try {
          console.log("Triggering reevaluation of pending requests...");
          const reevalData = await apiService.reevaluateRequests();

          console.log("Reevaluation completed:", reevalData.data);

          if (reevalData.success) {
            telemetryService.capture('admin_approval_mode_changed', currentUserId || '', {
              properties: {
                old_mode: settings?.notificationSettings?.approvalMode || 'basic',
                new_mode: mode,
                impacted_requests_count: approvalModeConfirmModal.impactedRequests.length || 0,
                auto_reevaluated: true,
              }
            });
          }

          if (reevalData.data && (reevalData.data.approved > 0 || reevalData.data.rejected > 0)) {
            showWarning(
              t('messages.reevaluationCompleted'),
              t('messages.reevaluationMessage', {
                processed: reevalData.data.processed,
                approved: reevalData.data.approved,
                rejected: reevalData.data.rejected
              }),
              "warning"
            );
          }
        } catch (reevalErr: any) {
          console.warn("Error during reevaluation:", reevalErr);
          // Ne pas bloquer l'UI si la réévaluation échoue
        }

        setSaveSuccess(true);
        // Masquer la notification après 3 secondes
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err: any) {
      console.error("Error saving approval mode:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const cancelApprovalModeChange = () => {
    setApprovalModeConfirmModal({
      isOpen: false,
      newMode: null,
      impactedRequests: []
    });
  };

  // ==================== QUOTA MODEL HANDLERS ====================

  const handleChangeQuotaMode = async (mode: StorageQuotaMode) => {
    if (!settings) return;

    const currentMode = settings.storageQuotaMode ?? 'NoQuota';

    // Si on passe à NoQuota depuis un autre mode, demander confirmation
    if (mode === 'NoQuota' && currentMode !== 'NoQuota') {
      setShowNoQuotaConfirmModal(true);
      return;
    }

    await applyQuotaModeChange(mode);
  };

  // ==================== SENSITIVITY LABELS HANDLERS ====================

  const handleChangeSensitivityLabelMode = async (mode: SensitivityLabelMode) => {
    console.log("[Admin] Saving sensitivity label mode : ", mode);
    if (!settings) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      const newSettings: AppSettings = { ...settings, sensitivityLabelMode: mode };
      const resp = await apiService.updateSettings(newSettings);
      if (resp.success && resp.data) {
        setSettings(resp.data);
        setSaveSuccess(true);
        telemetryService.capture('admin_sensitivity_label_mode_changed', currentUserId || '', {
          properties: {
            old_mode: settings?.sensitivityLabelMode || 'Disabled',
            new_mode: mode,
          }
        });
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err: any) {
      console.error("Error saving sensitivity label mode:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const applyQuotaModeChange = async (mode: StorageQuotaMode) => {
    console.log("[Admin] Saving quota mode : ", mode);
    if (!settings) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      const newSettings: AppSettings = { ...settings, storageQuotaMode: mode };
      const resp = await apiService.updateSettings(newSettings);
      if (resp.success && resp.data) {
        setSettings(resp.data);
        setSaveSuccess(true);
        telemetryService.capture('admin_quota_mode_changed', currentUserId || '', {
          properties: {
            old_mode: settings?.storageQuotaMode || 'NoQuota',
            new_mode: mode,
          }
        });
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err: any) {
      console.error("Error saving quota mode:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmNoQuotaChange = async () => {
    setShowNoQuotaConfirmModal(false);
    await applyQuotaModeChange('NoQuota');
  };

  const cancelNoQuotaChange = () => {
    setShowNoQuotaConfirmModal(false);
  };

  const openQuotaCreateModal = () => {
    setEditingQuotaModel(null);
    setQuotaForm({
      name: "",
      maxStorageGB: 100,
      notificationsEnabled: false,
      notificationThresholdPercent: null,
      isDefault: false,
    });
    setQuotaFormError(null);
    setShowQuotaModal(true);
  };

  const openQuotaEditModal = (model: QuotaModel) => {
    setEditingQuotaModel(model);
    setQuotaForm({
      name: model.name,
      maxStorageGB: model.maxStorageGB,
      notificationsEnabled: model.notificationsEnabled,
      notificationThresholdPercent: model.notificationThresholdPercent,
      isDefault: model.isDefault,
    });
    setQuotaFormError(null);
    setShowQuotaModal(true);
  };

  const closeQuotaModal = () => {
    setShowQuotaModal(false);
    setEditingQuotaModel(null);
    setQuotaFormError(null);
  };

  const reloadQuotaModels = async () => {
    const quotaDetail = extensionsStatus?.featureDetails?.find(f => f.key === FEATURE_KEYS.QUOTA);
    const isEnabled = quotaDetail ? quotaDetail.enabled : isQuotaFeatureEnabled;
    const isPremium = quotaDetail?.isPremium ?? false;
    const isPaid = extensionsStatus?.isPaidTier ?? false;
    if (!isEnabled || (isPremium && !isPaid)) return;

    setLoadingQuotaModels(true);
    try {
      const resp = await apiService.listQuotaModels();
      if (resp.success && resp.data) {
        setQuotaModels(resp.data);
      }
    } finally {
      setLoadingQuotaModels(false);
    }
  };

  const handleSaveQuotaModel = async () => {
    // Client-side validation
    const trimmedName = quotaForm.name.trim();
    if (!trimmedName) {
      setQuotaFormError(t('sections.modals.quotaModel.errors.nameRequired'));
      return;
    }
    const gb = quotaForm.maxStorageGB;
    if (!Number.isInteger(gb) || gb < 1 || gb > 25600) {
      setQuotaFormError(t('sections.modals.quotaModel.errors.maxStorageInvalid'));
      return;
    }
    let threshold: number | null = null;
    if (quotaForm.notificationsEnabled) {
      const p = quotaForm.notificationThresholdPercent;
      if (p === null || p === undefined || !Number.isInteger(p) || p < 0 || p > 100) {
        setQuotaFormError(t('sections.modals.quotaModel.errors.thresholdInvalid'));
        return;
      }
      threshold = p;
    }

    const payload: QuotaModelCreateInput = {
      name: trimmedName,
      maxStorageGB: gb,
      notificationsEnabled: quotaForm.notificationsEnabled,
      notificationThresholdPercent: threshold,
      isDefault: quotaForm.isDefault ?? false,
    };

    setQuotaFormSaving(true);
    setQuotaFormError(null);
    try {
      const resp = editingQuotaModel
        ? await apiService.updateQuotaModel(editingQuotaModel.id, payload)
        : await apiService.createQuotaModel(payload);

      if (resp.success) {
        if (editingQuotaModel) {
          telemetryService.capture('admin_quota_model_updated', currentUserId || '', {
            properties: {
              model_id: editingQuotaModel.id,
              model_name: trimmedName,
              max_storage_gb: gb,
              is_default: payload.isDefault ?? false,
            }
          });
        } else {
          telemetryService.capture('admin_quota_model_created', currentUserId || '', {
            properties: {
              model_name: trimmedName,
              max_storage_gb: gb,
              is_default: payload.isDefault ?? false,
              notifications_enabled: payload.notificationsEnabled,
            }
          });
        }
        await reloadQuotaModels();
        closeQuotaModal();
      } else {
        setQuotaFormError(resp.error || t('messages.recordSaveErrorMessage'));
      }
    } catch (err: any) {
      console.error("Error saving quota model:", err);
      setQuotaFormError(t('messages.recordSaveUnexpectedError'));
    } finally {
      setQuotaFormSaving(false);
    }
  };

  const handleDeleteQuotaModel = async () => {
    if (!editingQuotaModel) return;
    setQuotaFormSaving(true);
    try {
      const resp = await apiService.deleteQuotaModel(editingQuotaModel.id);
      if (resp.success) {
        telemetryService.capture('admin_quota_model_deleted', currentUserId || '', {
          properties: {
            model_id: editingQuotaModel.id,
            model_name: editingQuotaModel.name,
          }
        });
        await reloadQuotaModels();
        setShowDeleteConfirmModal(false);
        closeQuotaModal();
      } else {
        setQuotaFormError(resp.error || t('messages.recordSaveErrorMessage'));
        setShowDeleteConfirmModal(false);
      }
    } catch (err: any) {
      console.error("Error deleting quota model:", err);
      setQuotaFormError(t('messages.recordSaveUnexpectedError'));
      setShowDeleteConfirmModal(false);
    } finally {
      setQuotaFormSaving(false);
    }
  };

  const handleAddApprover = async (user: GraphUser) => {
    if (!settings || selectedApprovers.find(u => u.id === user.id)) return;
    
    const newApprovers = [...selectedApprovers, user];
    setSelectedApprovers(newApprovers);
    setShowPeoplePicker(false);
    
    // Sauvegarder immédiatement
    setSaving(true);
    try {
      const newSettings: AppSettings = {
        ...settings,
        approvers: newApprovers.map(u => ({ id: u.id, email: u.mail || u.userPrincipalName, displayName: u.displayName, addedDate: new Date().toISOString() }))
      };
      const resp = await apiService.updateSettings(newSettings);
      if (resp.success && resp.data) {
        setSettings(resp.data);
        setSaveSuccess(true);
        telemetryService.capture('admin_approver_added', currentUserId || '', {
          properties: {
            approver_count_after: newApprovers.length,
            approver_id: user.id,
          }
        });
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        // L'API a retourné une erreur
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err) {
      console.error("Error adding approver:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveApprover = async (userId: string) => {
    if (!settings) return;

    // Empêcher la suppression du dernier approbateur
    if (selectedApprovers.length <= 1) {
      showWarning(
        t('messages.cannotRemoveLastApprover'),
        t('messages.cannotRemoveLastApproverMessage'),
        "error"
      );
      return;
    }

    const newApprovers = selectedApprovers.filter(u => u.id !== userId);
    setSelectedApprovers(newApprovers);

    // Sauvegarder immédiatement
    setSaving(true);
    try {
      const newSettings: AppSettings = {
        ...settings,
        approvers: newApprovers.map(u => ({ id: u.id, email: u.mail || u.userPrincipalName, displayName: u.displayName, addedDate: new Date().toISOString() }))
      };
      const resp = await apiService.updateSettings(newSettings);
      if (resp.success && resp.data) {
        setSettings(resp.data);
        setSaveSuccess(true);
        telemetryService.capture('admin_approver_removed', currentUserId || '', {
          properties: {
            approver_count_after: newApprovers.length,
            removed_approver_id: userId,
          }
        });
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        // L'API a retourné une erreur
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err) {
      console.error("Error removing approver:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  // ==================== GESTION DES ADMINISTRATEURS ====================

  const handleAddAdministrator = async (user: GraphUser) => {
    if (administrators.find(a => a.id === user.id)) return;

    setSaving(true);
    try {
      const newAdmin: Administrator = {
        id: user.id,
        email: user.mail || user.userPrincipalName,
        displayName: user.displayName,
        addedDate: new Date().toISOString()
      };

      const resp = await apiService.addAdministrator(newAdmin);
      if (resp.success && resp.data) {
        setAdministrators([...administrators, resp.data]);
        setShowAdminPeoplePicker(false);
        setSaveSuccess(true);
        telemetryService.capture('admin_administrator_added', currentUserId || '', {
          properties: {
            admin_count_after: administrators.length + 1,
            new_admin_id: user.id,
          }
        });
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err) {
      console.error("Error adding administrator:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAdministrator = async (adminId: string) => {
    // Ne pas permettre de supprimer le dernier administrateur
    if (administrators.length === 1) {
      showWarning(
        t('messages.cannotRemoveLastAdmin'),
        t('messages.cannotRemoveLastAdminMessage'),
        "warning"
      );
      return;
    }

    // Ne pas permettre de se retirer soi-même
    if (currentUserId && adminId === currentUserId) {
      showWarning(
        t('messages.cannotRemoveSelf'),
        t('messages.cannotRemoveSelfMessage'),
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      const response = await apiService.removeAdministrator(adminId);

      if (!response.success) {
        // Si l'API retourne une erreur (par exemple auto-suppression détectée côté serveur)
        showWarning(
          t('messages.recordSaveError'),
          response.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
        return;
      }

      setAdministrators(administrators.filter(a => a.id !== adminId));
      setSaveSuccess(true);
      telemetryService.capture('admin_administrator_removed', currentUserId || '', {
        properties: {
          admin_count_after: administrators.length - 1,
          removed_admin_id: adminId,
        }
      });
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error("Error removing administrator:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePrivacyType = async (typeId: TeamPrivacy) => {
    if (!settings) return;

    // Ne pas permettre de désactiver le dernier type
    if (enabledPrivacyTypes.length === 1 && enabledPrivacyTypes.includes(typeId)) {
      showWarning(
        t('messages.cannotDisableLastPrivacyType'),
        t('messages.cannotDisableLastPrivacyTypeMessage'),
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      const newEnabledTypes = enabledPrivacyTypes.includes(typeId)
        ? enabledPrivacyTypes.filter(t => t !== typeId)
        : [...enabledPrivacyTypes, typeId];

      const newSettings: AppSettings = {
        ...settings,
        enabledPrivacyTypes: newEnabledTypes,
      };

      const resp = await apiService.updateSettings(newSettings);
      if (resp.success && resp.data) {
        setSettings(resp.data);
        setEnabledPrivacyTypes(newEnabledTypes);
        setSaveSuccess(true);
        telemetryService.capture('admin_privacy_type_toggled', currentUserId || '', {
          properties: {
            privacy_type: typeId,
            enabled: !enabledPrivacyTypes.includes(typeId),
            enabled_count_total: newEnabledTypes.length,
          }
        });
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        // L'API a retourné une erreur
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err: any) {
      console.error("Error toggling privacy type:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className={`setup-page theme-${theme}`}>
        <div className="setup-loading">
          <div className="spinner"></div>
          <p>{t('messages.loading', 'Chargement des informations...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`setup-page theme-${theme}`}>
        <div className="setup-header">
          <h1>{t('title')}</h1>
        </div>
        <div className="setup-section">
          <div className="errors-section">
            <p>⚠️ {error}</p>
            <p className="admin-section-margin">
              {t('messages.error', 'Assurez-vous que l\'application est correctement installée dans une équipe Teams.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`setup-page theme-${theme}`}>
      {/* Notification de succès - Position sticky sous le menu de navigation */}
      {saveSuccess && (
        <div className="success-card admin-success-sticky">
          <span className="icon-text">✓</span>
          <span>{t('messages.saved', 'Paramètres enregistrés avec succès')}</span>
        </div>
      )}

      <div className="setup-header">
        <h1>{t('title')}</h1>
        <p>{t('header.description')}</p>
      </div>

      {/* Subscription Management Card */}
      <SubscriptionCard onLicenseActivated={loadSettings} />

      {/* Restriction de création des groupes & équipes (Teams Creation Lock) */}
      {isGroupLockFeatureEnabled && (
        <SecurityGroupSection
          extensionsStatus={extensionsStatus}
          allowedSecurityGroup={allowedSecurityGroup}
          saving={saving}
          verifyingExtension={verifyingExtension}
          onActivateExtension={handleActivateExtension}
          onVerifyExtension={handleVerifyExtension}
          onOpenPicker={() => setShowSecurityGroupPicker(true)}
          onShowRemoveConfirm={() => setShowRemoveSecurityGroupConfirm(true)}
        />
      )}

{/* Création automatique des équipes */}
      <div className="setup-section">
        <h2>{t('sections.autoCreate.title')}</h2>
        <p className="help-text">
          {t('sections.autoCreate.description')}
        </p>

        <div className="status-card">
          <div className="status-content">
            <div className="checkbox-container">
              <input
                type="checkbox"
                id="autoCreateOnApproval"
                checked={settings?.autoCreateOnApproval ?? true}
                onChange={async (e) => {
                  if (!settings) return;
                  setSaving(true);
                  setSaveSuccess(false);
                  try {
                    const newSettings = { ...settings, autoCreateOnApproval: e.target.checked };
                    const resp = await apiService.updateSettings(newSettings);
                    if (resp.success && resp.data) {
                      setSettings(resp.data);
                      setSaveSuccess(true);
                      telemetryService.capture('admin_auto_create_toggled', currentUserId || '', {
                        properties: {
                          auto_create_enabled: e.target.checked,
                        }
                      });
                      setTimeout(() => setSaveSuccess(false), 3000);
                    } else {
                      showWarning(
                        t('messages.recordSaveError'),
                        resp.error || t('messages.recordSaveErrorMessage'),
                        "error"
                      );
                    }
                  } catch (err) {
                    console.error("Error saving auto-create setting:", err);
                    showWarning(
                      t('messages.recordSaveError'),
                      t('messages.recordSaveUnexpectedError'),
                      "error"
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                className="admin-checkbox-width"
              />
              <label htmlFor="autoCreateOnApproval" className="checkbox-label">
                {t('sections.autoCreate.label')}
              </label>
            </div>

            <div className="section-description">
              <strong>{t('sections.autoCreate.enabledDescription').split('\n')[0]}</strong><br />
              {t('sections.autoCreate.enabledDescription').split('\n')[1]}
            </div>

            <div className="section-description-error">
              <strong>{t('sections.autoCreate.disabledDescription').split('\n')[0]}</strong><br />
              {t('sections.autoCreate.disabledDescription').split('\n')[1]}
            </div>

            {saving && (
              <div className="admin-saving-msg">
                <div className="admin-spinner-inline"></div>
                <span>{t('messages.saving')}</span>
              </div>
            )}

            {saveSuccess && !saving && (
              <div className="admin-success-msg">
                ✓ {t('messages.saved')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Types d'équipes disponibles */}
      <PrivacyTypesSection
        allPrivacyTypes={allPrivacyTypes}
        enabledPrivacyTypes={enabledPrivacyTypes}
        saving={saving}
        saveSuccess={saveSuccess}
        onTogglePrivacyType={handleTogglePrivacyType}
      />

      {/* Modèles de quota de stockage */}
      {isQuotaFeatureEnabled && (
        <QuotaModelsSection
          settings={settings}
          extensionsStatus={extensionsStatus}
          quotaModels={quotaModels}
          loadingQuotaModels={loadingQuotaModels}
          saving={saving}
          verifyingExtension={verifyingExtension}
          onActivateExtension={handleActivateExtension}
          onVerifyExtension={handleVerifyExtension}
          onChangeQuotaMode={handleChangeQuotaMode}
          onOpenCreateModal={openQuotaCreateModal}
          onOpenEditModal={openQuotaEditModal}
        />
      )}

      {/* Étiquettes de sensibilité Microsoft Purview */}
      {isSensitivityFeatureEnabled && (
        <SensitivityLabelsSection
          settings={settings}
          extensionsStatus={extensionsStatus}
          saving={saving}
          verifyingExtension={verifyingExtension}
          onActivateExtension={handleActivateExtension}
          onVerifyExtension={handleVerifyExtension}
          onChangeSensitivityLabelMode={handleChangeSensitivityLabelMode}
        />
      )}

      {/* Gestion des administrateurs */}
      <AdministratorsSection
        administrators={administrators}
        currentUserId={currentUserId}
        saving={saving}
        onOpenPeoplePicker={() => setShowAdminPeoplePicker(true)}
        onRemoveAdministrator={handleRemoveAdministrator}
      />

      {/* Gestion des approbateurs */}
      <ApproversSection
        settings={settings}
        selectedApprovers={selectedApprovers}
        saving={saving}
        onChangeApprovalMode={onChangeApprovalMode}
        onOpenPeoplePicker={() => setShowPeoplePicker(true)}
        onRemoveApprover={handleRemoveApprover}
      />

      {/* Informations importantes */}
      <div className="setup-section">
        <div className="modal-section-info admin-how-it-works">
          <h3>
            {t('sections.howItWorks.title')}
          </h3>
          <ul>
            <li>
              <strong>{t('sections.howItWorks.whoCanRequest').split('?')[0]}?</strong> {t('sections.howItWorks.whoCanRequest').split('?')[1]}
            </li>
            <li>
              <strong>{t('sections.howItWorks.whoApproves').split('?')[0]}?</strong> {t('sections.howItWorks.whoApproves').split('?')[1]}
            </li>
            <li>
              <strong>{t('sections.howItWorks.howToApprove').split('?')[0]}?</strong> {t('sections.howItWorks.howToApprove').split('?')[1]}
            </li>
            <li className="admin-how-it-works-last">
              <strong>{t('sections.howItWorks.notifications').split(':')[0]}:</strong> {t('sections.howItWorks.notifications').split(':')[1]}
            </li>
          </ul>
        </div>
      </div>

      {/* PeoplePicker pour ajouter des approbateurs */}
      {showPeoplePicker && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h3 className="modal-title">
                  {t('sections.modals.selectApprovers.title')}
                </h3>
                <p className="modal-subtitle">
                  {t('sections.modals.selectApprovers.subtitle')}
                </p>
              </div>
              <button
                onClick={() => setShowPeoplePicker(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-body-paragraph">
                {t('sections.modals.selectApprovers.description')}
              </p>
              <PeoplePicker
                selectedUser=""
                onUserSelect={(user) => {
                  console.log("PeoplePicker user selected:", user);
                  handleAddApprover({
                    id: user.id,
                    displayName: user.displayName,
                    mail: user.mail || user.email || "",
                    userPrincipalName: user.userPrincipalName || ""
                  });
                  setShowPeoplePicker(false);
                }}
                placeholder={t('sections.modals.selectApprovers.placeholder')}
              />
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowPeoplePicker(false)}
                className="btn-tertiary"
              >
                {t('sections.modals.selectApprovers.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PeoplePicker pour ajouter des administrateurs */}
      {showAdminPeoplePicker && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-box">
            <div className="admin-modal-header">
              <div>
                <h3 className="admin-modal-title">
                  {t('sections.modals.selectAdministrators.title')}
                </h3>
                <p className="admin-modal-subtitle">
                  {t('sections.modals.selectAdministrators.subtitle')}
                </p>
              </div>
              <button
                onClick={() => setShowAdminPeoplePicker(false)}
                className="admin-modal-close-btn"
              >
                ×
              </button>
            </div>
            <div className="admin-modal-body">
              <p className="admin-modal-text">
                {t('sections.modals.selectAdministrators.description')}
              </p>
              <PeoplePicker
                selectedUser=""
                onUserSelect={(user) => {
                  console.log("Admin PeoplePicker user selected:", user);
                  handleAddAdministrator({
                    id: user.id,
                    displayName: user.displayName,
                    mail: user.mail || user.email || "",
                    userPrincipalName: user.userPrincipalName || ""
                  });
                }}
                placeholder={t('sections.modals.selectAdministrators.placeholder')}
              />
            </div>
            <div className="admin-modal-footer">
              <button
                onClick={() => setShowAdminPeoplePicker(false)}
                className="admin-modal-btn-cancel"
              >
                {t('sections.modals.selectAdministrators.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'avertissement */}
      {warningModal.isOpen && (
        <div className="admin-modal-overlay high-z">
          <div className="admin-modal-box small">
            <div className="admin-warning-content">
              <div className="admin-warning-icon">
                {warningModal.type === "warning" ? "⚠️" : "❌"}
              </div>
              <div className="admin-warning-body">
                <h3 className="admin-warning-title">
                  {warningModal.title}
                </h3>
                <p className="admin-warning-message">
                  {warningModal.message}
                </p>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button
                onClick={closeWarningModal}
                className="admin-modal-btn-confirm"
              >
                {t('sections.modals.warning.understood')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de changement de mode d'approbation */}
      {approvalModeConfirmModal.isOpen && (
        <div className="admin-modal-overlay confirmation-z">
          <div className="admin-modal-box confirmation">
            <h2 className="admin-modal-title large">
              {t('sections.modals.confirmApprovalMode.title')}
            </h2>

            <div className="admin-confirm-warning-box">
              <p className="admin-confirm-text">
                <strong>{t('sections.modals.confirmApprovalMode.warning').split(':')[0]}:</strong> {t('sections.modals.confirmApprovalMode.warning').split(':')[1]}
              </p>
            </div>

            {approvalModeConfirmModal.impactedRequests.length > 0 ? (
              <>
                <h3 className="admin-confirm-section-title">
                  {t('sections.modals.confirmApprovalMode.impactedRequests', { count: approvalModeConfirmModal.impactedRequests.length })}
                </h3>

                <div className="admin-confirm-list">
                  {approvalModeConfirmModal.impactedRequests.map((request, index) => (
                    <div
                      key={request.id}
                      className={`admin-confirm-item ${index % 2 === 0 ? 'even' : 'odd'}`}
                    >
                      <div className="admin-confirm-item-title">
                        {request.teamName}
                      </div>
                      <div className="admin-confirm-item-detail">
                        {t('sections.modals.confirmApprovalMode.requesterLabel')} : {request.requesterEmail}
                      </div>
                      <div className="admin-confirm-item-detail">
                        {getPrivacyLabel(request.privacy)}
                      </div>
                      <div className="admin-confirm-item-detail">
                        {t('sections.modals.confirmApprovalMode.approvalsLabel', { approved: request.approvedCount, total: request.totalApprovers })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="admin-confirm-empty">
                <p className="admin-confirm-empty-text">
                  {t('sections.modals.confirmApprovalMode.noImpactedRequests')}
                </p>
              </div>
            )}

            <div className="admin-modal-footer">
              <button
                onClick={cancelApprovalModeChange}
                className="admin-modal-btn-secondary"
              >
                {t('sections.modals.confirmApprovalMode.cancel')}
              </button>
              <button
                onClick={() => confirmApprovalModeChange(approvalModeConfirmModal.newMode!)}
                className="admin-modal-btn-confirm"
              >
                {t('sections.modals.confirmApprovalMode.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de création/édition d'un modèle de quota */}
      {showQuotaModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-box">
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">
                {editingQuotaModel
                  ? t('sections.modals.quotaModel.editTitle')
                  : t('sections.modals.quotaModel.createTitle')}
              </h3>
              <p className="admin-modal-subtitle">{t('sections.modals.quotaModel.subtitle')}</p>
              <button onClick={closeQuotaModal} className="admin-modal-close-btn">×</button>
            </div>

            <div className="admin-modal-body">
              <label>{t('sections.modals.quotaModel.fields.name')}</label>
              <input
                type="text"
                value={quotaForm.name}
                onChange={e => setQuotaForm({ ...quotaForm, name: e.target.value })}
              />

              <label>{t('sections.modals.quotaModel.fields.maxStorageGB')}</label>
              <input
                type="number"
                min={1}
                max={25600}
                value={quotaForm.maxStorageGB}
                onChange={e => setQuotaForm({ ...quotaForm, maxStorageGB: parseInt(e.target.value, 10) || 0 })}
              />

              <label>
                <input
                  type="checkbox"
                  checked={quotaForm.notificationsEnabled}
                  onChange={e => setQuotaForm({
                    ...quotaForm,
                    notificationsEnabled: e.target.checked,
                    notificationThresholdPercent: e.target.checked ? (quotaForm.notificationThresholdPercent ?? 80) : null,
                  })}
                />
                {t('sections.modals.quotaModel.fields.notificationsEnabled')}
              </label>

              {quotaForm.notificationsEnabled && (
                <>
                  <label>{t('sections.modals.quotaModel.fields.notificationThresholdPercent')}</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={quotaForm.notificationThresholdPercent ?? 80}
                    onChange={e => setQuotaForm({ ...quotaForm, notificationThresholdPercent: parseInt(e.target.value, 10) })}
                  />
                </>
              )}

              <label>
                <input
                  type="checkbox"
                  checked={quotaForm.isDefault ?? false}
                  onChange={e => setQuotaForm({ ...quotaForm, isDefault: e.target.checked })}
                />
                {t('sections.modals.quotaModel.fields.isDefault')}
              </label>

              {quotaFormError && (
                <div className="errors-section">
                  <p>{quotaFormError}</p>
                </div>
              )}
            </div>

            <div className="admin-modal-footer">
              {editingQuotaModel && (
                <button
                  onClick={() => setShowDeleteConfirmModal(true)}
                  disabled={quotaFormSaving}
                  className="admin-remove-btn"
                >
                  {t('sections.modals.quotaModel.delete')}
                </button>
              )}
              <button
                onClick={closeQuotaModal}
                disabled={quotaFormSaving}
                className="admin-modal-btn-cancel"
              >
                {t('sections.modals.quotaModel.cancel')}
              </button>
              <button
                onClick={handleSaveQuotaModel}
                disabled={quotaFormSaving}
                className="admin-modal-btn-confirm"
              >
                {quotaFormSaving ? t('messages.saving') : t('sections.modals.quotaModel.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de passage en mode "Aucun quota" */}
      {showNoQuotaConfirmModal && (
        <div className="admin-modal-overlay confirmation-z">
          <div className="admin-modal-box confirmation">
            <h2 className="admin-modal-title large">
              {t('sections.modals.confirmNoQuota.title')}
            </h2>
            <div className="admin-confirm-warning-box">
              <p className="admin-confirm-text">
                <strong>{t('sections.modals.confirmNoQuota.warning').split(':')[0]}:</strong>{t('sections.modals.confirmNoQuota.warning').split(':').slice(1).join(':')}
              </p>
            </div>
            <p>{t('sections.modals.confirmNoQuota.message')}</p>
            <div className="admin-modal-footer">
              <button
                onClick={cancelNoQuotaChange}
                disabled={saving}
                className="admin-modal-btn-secondary"
              >
                {t('sections.modals.confirmNoQuota.cancel')}
              </button>
              <button
                onClick={confirmNoQuotaChange}
                disabled={saving}
                className="admin-modal-btn-confirm"
              >
                {t('sections.modals.confirmNoQuota.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression d'un modèle de quota */}
      {showDeleteConfirmModal && editingQuotaModel && (
        <div className="admin-modal-overlay confirmation-z">
          <div className="admin-modal-box confirmation">
            <h2 className="admin-modal-title large">
              {t('sections.modals.quotaModel.deleteConfirmTitle')}
            </h2>
            <p>{t('sections.modals.quotaModel.deleteConfirmMessage', { name: editingQuotaModel.name })}</p>
            <div className="admin-modal-footer">
              <button
                onClick={() => setShowDeleteConfirmModal(false)}
                disabled={quotaFormSaving}
                className="admin-modal-btn-secondary"
              >
                {t('sections.modals.quotaModel.cancel')}
              </button>
              <button
                onClick={handleDeleteQuotaModel}
                disabled={quotaFormSaving}
                className="admin-modal-btn-confirm"
              >
                {t('sections.modals.quotaModel.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de sélection de groupe de sécurité */}
      {showSecurityGroupPicker && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h3 className="modal-title">
                  {t('sections.modals.selectSecurityGroup.title')}
                </h3>
                <p className="modal-subtitle">
                  {t('sections.modals.selectSecurityGroup.subtitle')}
                </p>
              </div>
              <button
                onClick={() => setShowSecurityGroupPicker(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-body-paragraph">
                {t('sections.modals.selectSecurityGroup.description')}
              </p>
              <PeoplePicker
                selectedUser=""
                searchType="groups"
                onUserSelect={(group) => {
                  console.log("Security Group selected:", group);
                  handleSetAllowedSecurityGroup({
                    id: group.id,
                    displayName: group.displayName,
                    mail: group.mail || group.email || "",
                    userPrincipalName: group.userPrincipalName || ""
                  });
                }}
                placeholder={t('sections.modals.selectSecurityGroup.placeholder')}
              />
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowSecurityGroupPicker(false)}
                className="btn-tertiary"
              >
                {t('sections.modals.selectSecurityGroup.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression de la restriction de groupe */}
      {showRemoveSecurityGroupConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h3 className="modal-title">
                  {t('sections.securityGroup.removeConfirmTitle')}
                </h3>
              </div>
              <button
                onClick={() => setShowRemoveSecurityGroupConfirm(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-body-paragraph">
                {t('sections.securityGroup.removeConfirmMessage')}
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowRemoveSecurityGroupConfirm(false)}
                className="btn-tertiary"
                disabled={saving}
              >
                {t('sections.securityGroup.cancel')}
              </button>
              <button
                onClick={handleRemoveAllowedSecurityGroup}
                className="admin-remove-btn"
                disabled={saving}
                style={{ height: '32px', padding: '0 12px' }}
              >
                {t('sections.securityGroup.confirmRemove')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
